/**
 * FavouritesWidget Store - Zustand store with individual selectors
 * Manages favourite pages data for header widget
 */

import { create } from 'zustand';
import type { FavouriteItem } from '@/application/queries/favourites';

// ============================================================================
// Store State
// ============================================================================

interface FavouritesWidgetState {
  // Data
  favourites: FavouriteItem[];
  loading: boolean;
  error: Error | null;

  // Actions
  setFavourites: (favourites: FavouriteItem[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: Error | null) => void;
  _reset: () => void;
}

// Empty array constant to prevent infinite re-renders
const EMPTY_FAVOURITES: FavouriteItem[] = [];

const initialState = {
  favourites: EMPTY_FAVOURITES,
  loading: false,
  error: null,
};

// ============================================================================
// Store
// ============================================================================

const useFavouritesWidgetStore = create<FavouritesWidgetState>((set) => ({
  ...initialState,

  setFavourites: (favourites) => set({ favourites, loading: false, error: null }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error, loading: false }),
  _reset: () => set(initialState),
}));

// ============================================================================
// Individual Selector Hooks
// ============================================================================

// Data selectors
export const useFavourites = () => useFavouritesWidgetStore((s) => s.favourites);

// State selectors
export const useFavouritesLoading = () => useFavouritesWidgetStore((s) => s.loading);
export const useFavouritesError = () => useFavouritesWidgetStore((s) => s.error);

// Action selectors
export const useSetFavouritesData = () => useFavouritesWidgetStore((s) => s.setFavourites);
export const useSetFavouritesLoading = () => useFavouritesWidgetStore((s) => s.setLoading);
export const useSetFavouritesError = () => useFavouritesWidgetStore((s) => s.setError);

// Derived selector: check if current page is favourited
export const useIsCurrentPageFavourited = () =>
  useFavouritesWidgetStore((s) => {
    const currentUrl = window.location.pathname.replace(/#$/, ''); // Remove trailing #
    return s.favourites.some((f) => f.url === currentUrl);
  });

// For testing only
export const _resetFavouritesWidgetStore = () => useFavouritesWidgetStore.getState()._reset();
